import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarIcon, Clock, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { computeBookingPrice, NON_MEMBER_PAD } from '@/lib/readerPricing';
import { directBookReader, fetchReaderAvailability, type ReaderAvailability } from '@/services/readerApi';
import { CardOnFile, type SavedCard } from '@/components/billing/CardOnFile';
import { US_TZ_OPTIONS, defaultTzOption, type TzOption } from '@/lib/readerTimezone';
import type { Reader } from '@/types/reader';

interface InviteReaderDialogProps {
  open: boolean;
  onClose: () => void;
  reader: Reader | null;
  onInviteSent: (readerId: string, requestId: string) => void;
}

const HOURS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const MINUTES = ['00', '15', '30', '45'];

const SERVICE_TYPES = [
  { value: 'reader', label: 'Reader', rateField: 'reader_rate' as const },
  { value: 'session_director', label: 'Session Director', rateField: 'reader_director_rate' as const },
  { value: 'coach', label: 'Coach', rateField: 'reader_coach_rate' as const },
];

const SESSION_LENGTHS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
];

export const InviteReaderDialog: React.FC<InviteReaderDialogProps> = ({
  open,
  onClose,
  reader,
  onInviteSent,
}) => {
  const { toast } = useToast();
  const { user } = useAuthContext();
  const sub = useSubscription();
  // Most people looking for a reader want one today, so the form opens on today
  // at the next quarter hour rather than on an empty date and a fixed 12:00 PM
  // that is usually already in the past.
  const nextSlot = () => {
    const d = new Date(Date.now() + 15 * 60 * 1000);
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
    const h24 = d.getHours();
    return {
      date: d,
      hour: String(h24 % 12 === 0 ? 12 : h24 % 12).padStart(2, '0'),
      minute: String(d.getMinutes()).padStart(2, '0'),
      period: h24 >= 12 ? 'PM' : 'AM',
    };
  };
  const initial = nextSlot();
  const [date, setDate] = useState<Date | undefined>(initial.date);
  const [hour, setHour] = useState(initial.hour);
  const [minute, setMinute] = useState(initial.minute);
  const [period, setPeriod] = useState(initial.period);
  // Card on file. Declared with the other state: canSubmit reads it, and when this
  // sat below that line the const was in its temporal dead zone — the component
  // threw ReferenceError on every render and the whole readers page went black.
  const [savedCard, setSavedCard] = useState<SavedCard | null>(null);
  const [serviceType, setServiceType] = useState('reader');
  const [sessionLength, setSessionLength] = useState(15);
  const [tz, setTz] = useState<TzOption>(defaultTzOption());
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Requester identity — required on the invite so the reader knows who's
  // booking. Prefilled from the authed user profile when available; the
  // user can edit / fill in if the casting record is missing a name.
  const [requesterFirstName, setRequesterFirstName] = useState('');
  const [requesterLastName, setRequesterLastName] = useState('');

  // Calendly-backed availability. Read-only: picking a slot just fills the
  // fields below, and the booking still goes through Hey Joe. Entirely
  // optional — readers without a Calendly never see this.
  const [avail, setAvail] = useState<ReaderAvailability | null>(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [pickedSlot, setPickedSlot] = useState<string | null>(null);
  // The event type the chosen slot came from. Without it the panel could
  // advertise a 60-minute consult while the form below still said 30.
  const [pickedEventName, setPickedEventName] = useState<string | null>(null);
  // Which event type's times are on screen. Jen has four; stacking them all made
  // one long ragged scroll where the type headings got sliced by the container.
  const [openTypeUuid, setOpenTypeUuid] = useState<string | null>(null);
  const calendlyUrl = reader?.freelancer_profile?.calendly_url;

  useEffect(() => {
    if (!open || !reader?._id || !calendlyUrl) { setAvail(null); return; }
    let cancelled = false;
    setAvailLoading(true);
    fetchReaderAvailability(reader._id, tz.value, 14)
      .then(a => {
        if (cancelled) return;
        setAvail(a);
        const first = (a.eventTypes || []).find(e => e.days.length);
        setOpenTypeUuid(first ? first.uuid : null);
      })
      .finally(() => { if (!cancelled) setAvailLoading(false); });
    return () => { cancelled = true; };
  }, [open, reader?._id, calendlyUrl, tz.value]);

  // A slot carries its own duration, so selecting one sets the session length
  // too — that's what keeps the price honest against their real event type.
  const chooseSlot = (iso: string, durationMins: number | null, eventName?: string) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return;
    setPickedSlot(iso);
    setPickedEventName(eventName || null);
    setDate(d);
    let h = d.getHours();
    const per = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    // Zero-padded to match the HOURS options ('01'..'12'). Unpadded, 4 PM set
    // the hour to '4', which matches no <SelectItem>, so the control silently
    // kept whatever was there before while the minute and AM/PM updated around
    // it — a picked slot would load as the right minute at the wrong hour.
    setHour(String(h).padStart(2, '0'));
    setMinute(String(d.getMinutes()).padStart(2, '0'));
    setPeriod(per);
    if (durationMins) setSessionLength(durationMins);
  };

  // Sync requester fields from the profile when the dialog opens / user
  // changes, so a stale dialog instance picks up fresh data.
  useEffect(() => {
    if (!open) return;
    setRequesterFirstName(prev => prev || user?.first_name || '');
    setRequesterLastName(prev => prev || user?.last_name || '');
  }, [open, user?.first_name, user?.last_name]);

  const profile = reader?.freelancer_profile;
  // Default to something this reader actually offers. 'reader' was hard-coded,
  // so a coach-only profile opened on a disabled "Reader (not offered)" row.
  useEffect(() => {
    if (!open || !profile) return;
    const current = SERVICE_TYPES.find(s => s.value === serviceType);
    if (current && profile[current.rateField] != null) return;
    const first = SERVICE_TYPES.find(s => profile[s.rateField] != null);
    if (first) setServiceType(first.value);
  }, [open, profile, serviceType]);
  const rateField = SERVICE_TYPES.find(s => s.value === serviceType)?.rateField;
  const rate = rateField && profile ? profile[rateField] : undefined;
  const pricing = rate != null
    ? computeBookingPrice(rate, sessionLength / 15, {
        isSubscriber: sub.isSubscriber,
        tier: sub.tier,
        sessionsRemaining: sub.readerSessionsRemaining,
      })
    : null;

  const trimmedFirst = requesterFirstName.trim();
  const trimmedLast = requesterLastName.trim();

  // You can't book a session that already started. The calendar only ever blocked
  // whole days, so today at 9am was bookable at 5pm — the request went out, readers
  // got paged for a slot in the past, and the first person to notice was whoever
  // turned up to nothing.
  //
  // Compared in the zone the BOOKER chose, not the browser's: someone in LA
  // arranging a 7pm Central session is not booking the past, and someone who
  // switches the dropdown to Eastern may be.
  const nowInTz = new Date(new Date().toLocaleString('en-US', { timeZone: tz.value }));
  const to24 = (h: string, p: string) => (parseInt(h, 10) % 12) + (p === 'PM' ? 12 : 0);
  const minsOf = (h: string, m: string, p: string) => to24(h, p) * 60 + parseInt(m, 10);
  const nowMins = nowInTz.getHours() * 60 + nowInTz.getMinutes();
  const isToday = !!date && new Date(date).toDateString() === nowInTz.toDateString();
  // Only same-day options can be in the past; a future date is always fine.
  const slotIsPast = (h: string, m: string, p: string) => isToday && minsOf(h, m, p) <= nowMins;
  const pickedIsPast = slotIsPast(hour, minute, period);

  // A card is required to ask. The Checkout fallback still exists server-side for
  // other paths, but offering it HERE gave two bad outcomes: a button that said
  // "Book and pay" on a screen whose whole point is that asking costs nothing, and
  // a Stripe redirect that left an unpaid request sitting in My Requests when the
  // buyer backed out of it.
  const canSubmit = !!savedCard && !!date && !pickedIsPast && !!trimmedFirst && !!trimmedLast && !!pricing && !isSubmitting;

  // Books this reader outright and goes straight to Stripe. The old path
  // created an open request and fanned it out to the whole roster, which made no
  // sense once the talent had already chosen this person and one of their slots.
  const handleBook = async () => {
    if (!reader || !date || !trimmedFirst || !trimmedLast) return;
    setIsSubmitting(true);
    try {
      const { checkoutUrl } = (await directBookReader({
        readerId: reader._id,
        serviceType,
        sessionLength,
        sessionDate: format(date, 'yyyy-MM-dd'),
        sessionTime: `${hour}:${minute} ${period}`,
        timezone: tz.value,        // booker's zone — fixes the old reader-zone bug
        timezoneLabel: tz.label,
        locationType: 'Virtual',
        notes: notes.trim() || undefined,
        requesterFirstName: trimmedFirst,
        requesterLastName: trimmedLast,
      })) as { checkoutUrl?: string };
      // With a card on file the server takes no money and returns no Checkout —
      // it has asked the reader instead. Navigating to an undefined URL here is
      // what would have happened to the first person who saved a card.
      // canSubmit requires a saved card, so the server always takes the no-money
      // path and returns no URL. If one ever comes back, something upstream changed.
      if (!checkoutUrl) {
        toast({ title: 'Request sent', description: `We've asked ${reader.first_name}. Your card is authorized and charged only if they accept.` });
        onClose();
        setIsSubmitting(false);
        return;
      }
      // Leave isSubmitting set — we're navigating away, and clearing it would let
      // an impatient second click open a second checkout.
      window.location.href = checkoutUrl;
    } catch (err: any) {
      // The server tests the card before it asks anyone. A decline lands here, on
      // the actor's own screen, and nothing was sent to the reader.
      const declined = /declined|no card|verify this card/i.test(err?.message || '');
      toast({
        title: declined ? 'Your card was declined' : 'Could not start booking',
        description: declined ? `${err.message} ${reader?.first_name || 'The reader'} hasn't been asked.` : err.message,
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  if (!reader) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="sm:max-w-lg max-h-[85vh] overflow-y-auto"
        data-paper="beige"
        style={{
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
        }}
      >
        <DialogHeader>
          <DialogTitle
            className="font-hj-display"
            style={{ fontSize: 22, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em' }}
          >
            Define your session time
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Pick a time and service and you’re booked with them — you pay now and we’ll send
            you both the calendar invite. Times below come from their own calendar.
          </p>

          {/* Their real open times, straight from Calendly. Read-only: picking one
              fills the fields below — the booking and the payment happen here.
              This block previously sat INSIDE the first-name field, between its
              label and its input, which is what buried the field and made the
              dialog look broken. */}
          {calendlyUrl && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> Their open times
                <span style={{ fontWeight: 400, color: 'var(--hj-beige-ink-2)' }}>· from their Calendly · {tz.label}</span>
              </Label>
              {/* Beige-paper tokens, not the app-surface ones. This dialog scopes
                  itself to data-paper="beige"; --hj-surface/--hj-ink resolve to the
                  DARK theme here and rendered a black slab on cream. */}
              {/* No inner scroll: one type at a time and five days keeps this short
                  enough to show whole. The old scroller sliced the next type's
                  heading in half at the fold. */}
              <div style={{ borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-beige-deep)', border: '1px solid var(--hj-beige-line)', padding: 12 }}>
                {availLoading && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>Checking their calendar…</p>}

                {!availLoading && avail && !avail.available && (
                  <p style={{ margin: 0, fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
                    No open times showing right now — pick any time below and we’ll ask them.
                  </p>
                )}

                {!availLoading && avail?.available && (() => {
                  const types = avail.eventTypes.filter(e => e.days.length);
                  const open = types.find(t => t.uuid === openTypeUuid) || types[0];
                  if (!open) return null;
                  return (
                    <>
                      {/* Choose the session type first. Showing every type's calendar
                          at once is what made this feel like a wall of pills. */}
                      {types.length > 1 && (
                        <div className="flex flex-wrap gap-1.5" style={{ marginBottom: 10 }}>
                          {types.map(t => {
                            const on = t.uuid === open.uuid;
                            return (
                              <button
                                key={t.uuid}
                                type="button"
                                onClick={() => setOpenTypeUuid(t.uuid)}
                                style={{
                                  padding: '5px 12px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                                  fontWeight: on ? 700 : 500,
                                  background: on ? 'var(--hj-beige-ink)' : 'transparent',
                                  color: on ? 'var(--hj-beige)' : 'var(--hj-beige-ink-2)',
                                  border: '1px solid ' + (on ? 'var(--hj-beige-ink)' : 'var(--hj-beige-line)'),
                                }}
                              >
                                {t.durationMins ? `${t.durationMins} min` : t.name}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <p className="truncate" style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>{open.name}</p>

                      {/* Fixed label column + its own wrapping track for the chips.
                          A single flex row let overflow chips wrap flush-left under
                          the day label, which is what made the columns look broken. */}
                      {open.days.slice(0, 5).map(day => (
                        <div
                          key={day.date}
                          style={{ display: 'grid', gridTemplateColumns: '58px minmax(0, 1fr)', gap: 10, alignItems: 'start', padding: '5px 0', borderTop: '1px solid var(--hj-beige-line)' }}
                        >
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--hj-beige-ink-2)', paddingTop: 6 }}>
                            {new Date(day.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}
                          </span>
                          <div className="flex flex-wrap" style={{ gap: 5 }}>
                            {day.slots.map(iso => (
                              <button
                                key={iso}
                                type="button"
                                onClick={() => chooseSlot(iso, open.durationMins, open.name)}
                                style={{
                                  padding: '4px 9px', borderRadius: 7, fontSize: 11.5, cursor: 'pointer',
                                  fontWeight: pickedSlot === iso ? 700 : 500,
                                  background: pickedSlot === iso ? 'var(--hj-yellow, #FFD60A)' : 'transparent',
                                  border: '1px solid ' + (pickedSlot === iso ? 'var(--hj-yellow, #FFD60A)' : 'var(--hj-beige-line)'),
                                  color: 'var(--hj-beige-ink)', whiteSpace: 'nowrap',
                                }}
                              >
                                {new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      {open.days.length > 5 && (
                        <p style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>
                          They have {open.days.length - 5} more open day{open.days.length - 5 === 1 ? '' : 's'} after these. Don’t see one that works? Set any date and time using the fields below and we’ll ask them.
                        </p>
                      )}
                    </>
                  );
                })()}

              </div>
              {pickedSlot && pickedEventName && (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
                  Picked <strong style={{ color: 'var(--hj-beige-ink)' }}>{pickedEventName}</strong> — {sessionLength} min, {format(date!, 'MMM d')} at {hour}:{minute} {period}.
                </p>
              )}
            </div>
          )}

          {/* Requester name — required so the reader knows who's booking. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="requester-first">Your first name *</Label>
              <Input
                id="requester-first"
                value={requesterFirstName}
                onChange={(e) => setRequesterFirstName(e.target.value)}
                placeholder="Jane"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="requester-last">Your last name *</Label>
              <Input
                id="requester-last"
                value={requesterLastName}
                onChange={(e) => setRequesterLastName(e.target.value)}
                placeholder="Doe"
                required
              />
            </div>
          </div>

          {/* Date Picker */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><CalendarIcon className="w-4 h-4" /> Date & Time</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[180px] justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, 'MMM d, yyyy') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { setDate(d); setPickedSlot(null); setCalendarOpen(false); }}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  />
                </PopoverContent>
              </Popover>

              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <Select value={hour} onValueChange={(v) => { setHour(v); setPickedSlot(null); }}>
                  <SelectTrigger className="w-[70px]"><SelectValue /></SelectTrigger>
                  {/* An hour is only dead once even its last quarter has gone. */}
                  <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h} disabled={slotIsPast(h, '45', period)}>{h}</SelectItem>)}</SelectContent>
                </Select>
                <span>:</span>
                <Select value={minute} onValueChange={(v) => { setMinute(v); setPickedSlot(null); }}>
                  <SelectTrigger className="w-[70px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m} disabled={slotIsPast(hour, m, period)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={period} onValueChange={(v) => { setPeriod(v); setPickedSlot(null); }}>
                  <SelectTrigger className="w-[70px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AM" disabled={slotIsPast('11', '45', 'AM')}>AM</SelectItem>
                    <SelectItem value="PM" disabled={slotIsPast('11', '45', 'PM')}>PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <Select value={tz.value} onValueChange={(v) => setTz(US_TZ_OPTIONS.find(o => o.value === v) || tz)}>
                <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {US_TZ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">your timezone</span>
            </div>
          </div>

          {/* Service Type */}
          <div className="space-y-2">
            <Label>Service Type</Label>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map(st => {
                  const r = profile?.[st.rateField];
                  return (
                    <SelectItem key={st.value} value={st.value} disabled={r == null}>
                      {st.label}{r != null ? ` ($${r.toFixed(2)}/15 min)` : ' (not offered)'}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Session Length */}
          <div className="space-y-2">
            <Label>Session Length</Label>
            <Select value={String(sessionLength)} onValueChange={(v) => setSessionLength(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SESSION_LENGTHS.map(sl => (
                  <SelectItem key={sl.value} value={String(sl.value)}>{sl.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details for the reader..."
              rows={3}
            />
          </div>

          {/* The real total, because this books and charges outright. It used to
              read "you're not charged to request" — true of the broadcast flow, and
              badly wrong on a screen where you've already chosen the person, the
              service and one of their actual open slots. */}
          <div
            style={{
              padding: 14,
              borderRadius: 'var(--hj-r-sm)',
              background: 'var(--hj-beige-deep)',
              border: '1px solid var(--hj-beige-line)',
              fontSize: 12.5,
              lineHeight: 1.5,
              color: 'var(--hj-beige-ink-2)',
            }}
          >
            {pricing ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 15, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>
                  <span>Charged if {reader.first_name} accepts</span>
                  <span>${pricing.total.toFixed(2)}</span>
                </div>
                <div style={{ marginTop: 6 }}>
                  {sessionLength} min with {reader.first_name}
                  {rate != null ? ` · $${rate.toFixed(2)}/15 min` : ''}
                  {pricing.padApplied ? ` · incl. $${pricing.pad} non-member fee` : ''}
                  {pricing.padWaived ? ' · included with your membership' : ''}
                </div>
                {!sub.isSubscriber && <div style={{ marginTop: 4 }}>Plus members skip the ${NON_MEMBER_PAD} non-member fee.</div>}
              </>
            ) : (
              <>{reader.first_name} hasn’t set a rate for that service yet — pick another service type.</>
            )}
          </div>

          {/* Asking someone should not take their money. With a card filed here the
              request costs nothing and the charge happens only if this reader
              accepts; without one we still send them through Checkout. */}
          <div>
            <div className="mono-label" style={{ marginBottom: 6, color: 'var(--hj-beige-ink)' }}>
              {savedCard ? 'Card on file' : 'Add a card'}
            </div>
            <CardOnFile onCardChange={setSavedCard} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleBook} disabled={!canSubmit}>
            {isSubmitting
              ? 'Sending…'
              : !savedCard
                ? 'Add a card to continue'
                : pickedIsPast
                  ? 'Pick a time in the future'
                  : pricing
                    ? `Ask ${reader.first_name} ($${pricing.total.toFixed(2)} if they accept)`
                    : 'Book'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
